import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import ReactECharts from 'echarts-for-react';
import type { TelemetryPoint } from '@monitor/shared';
import { useSiteRealtime, useRealtimeStore } from '@/features/realtime/realtimeStore';
import { api } from '@/lib/axios';
import { useSiteSensors } from '@/features/sensors/hooks';
import { useZones } from '@/features/zones/hooks';
import { useBoards } from '@/features/boards/hooks';
import { useSite } from '@/features/sites/detailHooks';
import { dayjs } from '@/lib/dayjs';
import { useTheme } from '@/features/theme/useTheme';
import { CHART_THEMES } from '@/features/theme/chartTheme';
import {
  buildCompareOption,
  CHART_TICK_MS,
  COMPARE_METRIC_META,
  type ChartWindow,
  type CompareBy,
  type CompareMetric,
} from '@/features/dashboard/compareChart';

const WINDOW_LABELS: Record<ChartWindow, string> = {
  '30s': '30 วิ', '1m': '1 นาที', '5m': '5 นาที', '15m': '15 นาที', '30m': '30 นาที', '1h': '1 ชม.',
};
const METRICS: CompareMetric[] = ['voltage', 'current', 'power'];

/**
 * Stand-alone pop-out (opened via window.open from the dashboard). Shows the
 * zone/board comparison for ALL THREE metrics (V, A, W) at once so the operator
 * can watch them side by side on a second monitor. The app boots fresh in this
 * window, so useGlobalRealtime (in App.tsx) keeps the store live here too.
 */
export function ComparePopoutPage() {
  const { siteId } = useParams();
  const id = siteId ? Number(siteId) : null;
  const themeMode = useTheme();
  const ct = CHART_THEMES[themeMode];

  const site = useSiteRealtime(id ?? 0);
  const { data: siteInfo } = useSite(id);
  const { data: sensors = [] } = useSiteSensors(id);
  const { data: zones = [] } = useZones(id);
  const { data: boards = [] } = useBoards(id ?? undefined);

  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const [compareBy, setCompareBy] = useState<CompareBy>('zone');
  const [chartWindow, setChartWindow] = useState<ChartWindow>('15m');

  const activeSensors = useMemo(() => sensors.filter((s) => s.isActive), [sensors]);
  const countedIds = useMemo(
    () => new Set(activeSensors.filter((s) => s.countInTotal ?? s.isPrimary).map((s) => s.id)),
    [activeSensors],
  );

  // This window boots a fresh app, so the realtime store starts EMPTY — without
  // this the charts only fill in going forward. Backfill the last 6 h of history
  // per counted sensor so the chart is populated the moment the window opens.
  const seedSensorHistory = useRealtimeStore((s) => s.seedSensorHistory);
  const seededRef = useRef(false);
  useEffect(() => {
    if (id == null || seededRef.current) return;
    const counted = activeSensors.filter((s) => s.countInTotal ?? s.isPrimary);
    if (counted.length === 0) return; // sensors not loaded yet
    seededRef.current = true;
    const to = new Date().toISOString();
    const from = new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString();
    (async () => {
      for (const s of counted) {
        try {
          const res = await api.get<Array<{
            time: string; voltage: number | null; current: number | null;
            power: number | null; energy: number | null; temperature: number | null; humidity: number | null;
          }>>('/api/telemetry/series', { params: { siteId: id, sensorId: s.id, range: 'custom', from, to } });
          const pts: TelemetryPoint[] = (res.data ?? [])
            .filter((p) => p.power != null || p.voltage != null || p.current != null)
            .map((p) => ({
              time: p.time, sensorId: s.id, siteId: id,
              voltage: p.voltage, current: p.current, power: p.power,
              energy: p.energy, temperature: p.temperature, humidity: p.humidity ?? null,
            }));
          if (pts.length) seedSensorHistory(id, s.id, pts);
        } catch {
          // ignore — realtime will still fill in over time
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, activeSensors]);

  const charts = useMemo(
    () => METRICS.map((m) => ({
      metric: m,
      ...buildCompareOption({ bySensor: site?.bySensor, activeSensors, countedIds, zones, boards, compareBy, metric: m, chartWindow, now, ct }),
    })),
    [site, activeSensors, countedIds, zones, boards, compareBy, chartWindow, now, ct],
  );

  if (id == null) {
    return <div style={{ padding: 40, color: 'var(--dim)' }}>ไม่พบไซต์</div>;
  }

  const btn = (active: boolean): React.CSSProperties => ({
    background: active ? 'var(--cyan-glow-strong)' : 'var(--bg-input)',
    border: `1px solid ${active ? 'var(--cyan)' : 'var(--border-color)'}`,
    color: active ? 'var(--cyan)' : 'var(--text)',
    padding: '5px 12px', borderRadius: 6, cursor: 'pointer',
    fontSize: 12, fontWeight: active ? 700 : 500, fontFamily: 'inherit',
  });

  return (
    // Fit the viewport exactly — no page scroll. Toolbar keeps its natural
    // height; the three charts share the remaining space equally (flex:1 each).
    <div style={{ height: '100vh', boxSizing: 'border-box', overflow: 'hidden', background: 'var(--bg)', color: 'var(--text)', padding: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
      {/* Toolbar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', flexShrink: 0 }}>
        <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <span style={{ fontSize: 15, fontWeight: 800, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {siteInfo ? `${siteInfo.code} · ${siteInfo.name}` : 'เปรียบเทียบการใช้ไฟ'}
          </span>
          <span style={{ fontSize: 12, color: 'var(--dim)' }}>
            เปรียบเทียบการใช้ไฟ V·A·W · {dayjs(now).format('DD MMM BBBB · HH:mm:ss')}
          </span>
        </div>
        <div style={{ display: 'flex', gap: 4, marginLeft: 'auto' }}>
          {(['zone', 'board'] as const).map((m) => (
            <button key={m} onClick={() => setCompareBy(m)} style={btn(m === compareBy)}>
              {m === 'zone' ? 'ตามโซน' : 'ตามบอร์ด'}
            </button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {(Object.keys(CHART_TICK_MS) as ChartWindow[]).map((w) => (
            <button key={w} onClick={() => setChartWindow(w)} style={btn(w === chartWindow)}>
              {WINDOW_LABELS[w]}
            </button>
          ))}
        </div>
      </div>

      {/* Three stacked charts: V, A, W — share the leftover height equally */}
      {charts.map(({ metric, option, hasData }) => (
        <section key={metric} className="card" style={{ padding: '10px 14px', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 6, flexShrink: 0 }}>
            {COMPARE_METRIC_META[metric].label} ({COMPARE_METRIC_META[metric].unit})
          </div>
          <div style={{ flex: 1, minHeight: 0, position: 'relative' }}>
            {hasData ? (
              <ReactECharts key={`${compareBy}-${metric}`} option={option} style={{ height: '100%', width: '100%' }} lazyUpdate />
            ) : (
              <div style={{ height: '100%', display: 'grid', placeItems: 'center', color: 'var(--dim)', fontSize: 12 }}>
                ไม่มีข้อมูลในช่วงที่เลือก
              </div>
            )}
          </div>
        </section>
      ))}
    </div>
  );
}
